"use client";

import { GET_OFFER, CategoryType } from "@/graphql/queries/get-offer";
import { useQuery } from "@apollo/client/react";
import { Dialog, Typography } from "@repo/ui";
import { getCookie, setCookie } from "cookies-next/client";
import moment from "moment";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

const OFFER_POPUP_COOKIE = "offer-popup-closed";
const OFFER_POPUP_FIRST_SEEN_COOKIE = "offer-popup-first-seen";
const COOKIE_EXPIRY_HOURS = 24;
const FIRST_TIME_DELAY_MS = 15_000;

const OfferPopup = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const { data } = useQuery(GET_OFFER, {
    variables: {
      type: CategoryType.ALL_COUNTRIES_ONLY_COUPON,
    },
  });

  // Check cookie on mount and when data is available
  useEffect(() => {
    if (!data?.offerByCategory) return;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const closedTimestamp = getCookie(OFFER_POPUP_COOKIE);
    const hasSeenPopup = Boolean(getCookie(OFFER_POPUP_FIRST_SEEN_COOKIE));

    if (closedTimestamp) {
      const closedTime = parseInt(closedTimestamp as string, 10);
      const currentTime = Date.now();
      const hoursSinceClose = (currentTime - closedTime) / (1000 * 60 * 60);

      // Only show popup if more than 24 hours have passed
      if (!Number.isNaN(closedTime) && hoursSinceClose < COOKIE_EXPIRY_HOURS) {
        setOpen(false);
        return;
      }
    }

    if (!hasSeenPopup) {
      timer = setTimeout(() => {
        setOpen(true);
        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        setCookie(OFFER_POPUP_FIRST_SEEN_COOKIE, "true", {
          expires: expiryDate,
          sameSite: "lax",
        });
      }, FIRST_TIME_DELAY_MS);
    } else {
      setOpen(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [data?.offerByCategory]);

  const handleClose = (openState?: boolean) => {
    // If dialog is being closed (openState is false or undefined)
    if (openState === false || openState === undefined) {
      setOpen(false);
      // Set cookie with current timestamp, expires in 24 hours
      const expiryDate = new Date();
      expiryDate.setHours(expiryDate.getHours() + COOKIE_EXPIRY_HOURS);
      setCookie(OFFER_POPUP_COOKIE, Date.now().toString(), {
        expires: expiryDate,
        sameSite: "lax",
      });
    } else if (openState === true) {
      // If dialog is being opened, just update state
      setOpen(true);
    }
  };

  const offer = useMemo(() => {
    if (!data?.offerByCategory) return null;

    const endDate = moment(data.offerByCategory.endDate);
    return {
      ...data.offerByCategory,
      startDate: moment(data.offerByCategory.startDate).format("DD-MM-YYYY"),
      endDate: endDate.format("DD-MM-YYYY"),
      endDateParsed: {
        day: endDate.format("DD"),
        month: endDate.format("MMM"),
        year: endDate.format("YYYY"),
      },
      endDateMoment: endDate,
    };
  }, [data?.offerByCategory]);

  // Countdown timer effect
  useEffect(() => {
    if (!offer?.endDateMoment) return;

    const calculateTimeRemaining = () => {
      const now = moment();
      const end = offer.endDateMoment;
      const diff = end.diff(now);

      if (diff <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const duration = moment.duration(diff);
      setTimeRemaining({
        days: Math.floor(duration.asDays()),
        hours: duration.hours(),
        minutes: duration.minutes(),
        seconds: duration.seconds(),
      });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [offer?.endDateMoment]);

  if (data?.offerByCategory === null) return null;

  const handleCopy = async (code?: string) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (e) {
      console.log("e", e);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
      className="p-0"
      dismissible={false}
    >
      <div className="relative  mx-auto bg-white dark:bg-slate-900 rounded-xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
        {/* Media/visual panel (slightly larger) */}
        <div className="md:w-5/12 bg-primary-main flex items-center justify-center">
          {offer?.banner ? (
            <div className="w-full max-w-full h-[350px] rounded-lg overflow-hidden shadow-lg">
              <Image
                src={offer.banner}
                alt={offer.name || "offer"}
                width={440}
                height={440}
                className="object-fill w-full h-full"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-white">
              <div className="text-4xl font-extrabold">Special Offer</div>
              <div className="text-sm opacity-90">
                Exclusive deals just for you
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="md:w-7/12 p-4  md:p-6 flex flex-col justify-center gap-4">
          <Typography
            variant="h2"
            className="text-slate-900 dark:text-slate-100"
          >
            {offer?.name || "Limited Time Offer"}
          </Typography>

          <p className="text-sm text-slate-600 dark:text-slate-300">
            {offer?.description}
          </p>

          <div className="mt-4 p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50 border-2 border-dashed border-amber-400 dark:border-amber-700 rounded-lg">
            <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1.5">
              Coupon Code
            </div>
            <div className="bg-white dark:bg-slate-800 px-3 py-2 rounded border border-amber-200 dark:border-amber-800 flex items-center justify-between gap-2 group">
              <div className="font-mono text-base font-bold text-slate-900 dark:text-slate-100 tracking-wider flex-1">
                {offer?.couponCode || "—"}
              </div>
              <button
                onClick={() => handleCopy(offer?.couponCode)}
                className="flex-shrink-0 p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400"
                title={copied ? "Copied!" : "Copy code"}
              >
                {copied ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172l-3.293-3.293A1 1 0 003.293 9.293l4 4a1 1 0 001.414 0l8-8z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="bg-slate-800 dark:bg-slate-900 rounded-lg px-4 py-3 flex items-center justify-center gap-1">
              <span className="text-white dark:text-slate-100 text-sm font-medium mr-2">
                Ends in :
              </span>
              <div className="flex items-center gap-1 font-mono text-white dark:text-slate-100">
                <span className="text-base font-semibold">
                  {String(timeRemaining.days).padStart(2, "0")}D
                </span>
                <span className="mx-0.5">:</span>
                <span className="text-base font-semibold">
                  {String(timeRemaining.hours).padStart(2, "0")}H
                </span>
                <span className="mx-0.5">:</span>
                <span className="text-base font-semibold">
                  {String(timeRemaining.minutes).padStart(2, "0")}M
                </span>
                <span className="mx-0.5">:</span>
                <span className="text-base font-semibold">
                  {String(timeRemaining.seconds).padStart(2, "0")}S
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default OfferPopup;
